import {useMemo} from 'react';
import {useMySettings} from '_contexts/MySettingsProvider';
import {getColorsByMode} from '_themes/tokens/colors';
import {fontFamily} from '_themes/tokens/typography';
import {toolbar, zIndex} from '_themes/tokens/spacing';
import {createComponentOverrides} from '_themes/components';
import {useTranslation} from 'react-i18next';

import {createTheme, ThemeProvider} from '@mui/material';
import {enUS as coreEnUS, jaJP as coreJaJP} from '@mui/material/locale';
import {enUS, jaJP} from '@mui/x-data-grid-pro';

import type {} from '@mui/x-data-grid-pro/themeAugmentation';
export interface Props {
  children: React.ReactNode;
}

/**
 * LFCThemeProvider(V5)
 */
const LFCThemeProviderV5 = (props: Props) => {
  const {i18n} = useTranslation();
  const mySettings = useMySettings();

  const theme = useMemo(() => {
    const mode = mySettings.isDark ? 'dark' : 'light';
    const colors = getColorsByMode(mode);
    const localization = i18n.language === 'en' ? [enUS, coreEnUS] : [jaJP, coreJaJP];

    return createTheme(
      {
        typography: {
          fontFamily
        },
        mixins: {
          toolbar: {
            backgroundColor: 'inherit',
            minHeight: toolbar.minHeight,
            '@media (min-width:0px) and (orientation: landscape)': {
              minHeight: toolbar.landscape
            },
            '@media (min-width:600px)': {
              minHeight: toolbar.desktop
            }
          }
        },
        palette: {
          mode,
          primary: {
            main: colors.primary
          },
          secondary: {
            main: colors.secondary
          },
          text: colors.text,
          divider: colors.divider,
          background: colors.background,
          charts: colors.charts,
          scrollbar: colors.scrollbar,
          shadow: colors.shadow,
          backdrop: colors.backdrop
        },
        zIndex: {
          fab: zIndex.fab
        },
        components: createComponentOverrides(colors)
      },
      ...localization
    );
  }, [mySettings.isDark, i18n.language]);

  return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
};
export default LFCThemeProviderV5;
